import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { LayoutList } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useNetwork } from '../../contexts/NetworkContext'
import { CardContent, CardIcon } from '@/components/ui/card'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { getContext } from '@/services/networks/networksApi'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { AuthService } from '@/services/core/auth'
import { LineItemsApi, LineItem } from '@/services/lineItems/lineItemsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { AppShell } from '../../components/AppShell'
import { LineItemTable } from '../../components/LineItemTable'
import { LineItemFilters, useLineItemFilters } from '../../components/LineItemFilters'
import { BulkActionBar } from '../../components/BulkActionBar'
import { SearchInput } from '../../components/SearchInput'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function LineItemsPage() {
  const { t } = useTranslation()
  const { scopeKey, scopeReady } = useNetwork()
  const hasToken = AuthService.isAuthenticated()
  const queryClient = useQueryClient()
  const { filters, setFilters, fetchFilters, defaults } = useLineItemFilters()
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<LineItem[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const context = getContext()

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    LineItemsApi.getPage(page, pageSize, { ...fetchFilters, search }, sort)

  const { data, isLoading } = useQuery({
    queryKey: ['lineitems', context, scopeKey, filters, search, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: hasToken && scopeReady,
  })

  const lineItems = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  // Any change to what is being listed invalidates the current page number.
  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const handleFilters = (next: typeof filters) => {
    setFilters(next)
    setPage(1)
    setSelection([])
  }

  const handleSearch = (v: string) => {
    setSearch(v)
    setPage(1)
    setSelection([])
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(selection.map((li) => LineItemsApi.update(li.id, { name: li.name, objectState: state })))
      queryClient.invalidateQueries({ queryKey: ['lineitems'] })
      toast.success(t('common.done'), { duration: 3000 })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('lineItems.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('lineItems.title')}</h1>
            <p>{t('lineItems.subtitle')}</p>
          </div>
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)}>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={LayoutList} />
                {t('lineItems.lineItemCount', { count: totalCount })}
              </>
            }
            controls={
              <>
                <SearchInput value={search} onChange={handleSearch} />
                <LineItemFilters
                  filters={filters}
                  onChange={handleFilters}
                  defaults={defaults}
                  showCountry
                  serverFiltered
                />
              </>
            }
            actions={<PageSizeSelect value={pageSize} onChange={(s) => { setPageSize(s); setPage(1) }} />}
          />

          {selection.length > 0 && (
            <BulkActionBar
              count={selection.length}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
              onClear={() => setSelection([])}
            />
          )}

          <CardContent className="p-0">
            <LineItemTable
              lineItems={lineItems}
              loading={isLoading}
              selection={selection}
              onSelectionChange={setSelection}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('lineItems.lineItemCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
